import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import { getSessionDrawerChrome, uploadDialogPaperSx } from '@kangaroo/ui';

import { CircuitLapResliceSettings } from '../telemetry/CircuitLapResliceSettings';
import { CircuitSetupGuide } from './CircuitSetupGuide';
import type { useSessionLapSlot } from '../../features/sessions/useSessionLapSlot';
import { SessionCircuitPicker } from './SessionCircuitPicker';

type SessionCircuitSetupDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  slot: ReturnType<typeof useSessionLapSlot>;
};

export function SessionCircuitSetupDialog({
  open,
  onClose,
  title,
  slot,
}: SessionCircuitSetupDialogProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const chrome = getSessionDrawerChrome(isDark);
  const {
    circuitPickerOptions,
    assignedContribution,
    assignCircuit,
    canEdit,
    contributionsLoading,
    contributionsHydrating,
    circuitLapReslice,
    resolvedCircuit,
    suggestedCircuit,
    suggestedCircuitMatch,
    circuitSourceLabel,
    sessionContext,
  } = slot;

  const reslice = circuitLapReslice.activeReslice;
  const selectedContributionId = sessionContext?.contributionId ?? '';
  const startFinishStatus = resolvedCircuit.overlay?.startFinishLine
    ? ('ready' as const)
    : contributionsLoading || contributionsHydrating
      ? ('loading' as const)
      : ('missing' as const);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: uploadDialogPaperSx(isDark),
      }}
    >
      <DialogTitle sx={{ fontWeight: 900, pb: 1, color: chrome.title }}>{title}</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Stack spacing={1.5}>
          <Typography sx={{ fontSize: '0.72rem', color: chrome.body, lineHeight: 1.5 }}>
            Assign a saved circuit to this session, then split laps at the circuit start/finish
            line. Save fixed laps to reuse this timing on Telemetry and Sessions.
          </Typography>

          <CircuitSetupGuide
            assignedContribution={assignedContribution}
            startFinishStatus={startFinishStatus}
            lapCount={reslice?.laps.length ?? 0}
            hasSavedLaps={circuitLapReslice.hasSaved}
            onInspectMap={assignedContribution ? onClose : undefined}
          />

          {!canEdit && !slot.publicDemo ? (
            <Alert severity="info" sx={{ py: 0.35 }}>
              Session editing is read-only. Assigning circuits and saving lap timing requires an
              active subscription or partner code.
            </Alert>
          ) : slot.publicDemo ? (
            <Alert severity="info" sx={{ py: 0.35 }}>
              Demo reslices run locally. Upload your own session to save circuit lap timing.
            </Alert>
          ) : null}

          {suggestedCircuit && !sessionContext?.contributionId ? (
            <Alert
              severity="success"
              action={
                canEdit ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => void assignCircuit(suggestedCircuit.id)}
                    sx={{ textTransform: 'none' }}
                  >
                    Use match
                  </Button>
                ) : undefined
              }
            >
              Best match:{' '}
              {[suggestedCircuit.venue, suggestedCircuit.layoutName].filter(Boolean).join(' · ')}
              {suggestedCircuitMatch
                ? ` (${Math.round(suggestedCircuitMatch.confidence * 100)}% GPS/shape, ${Math.round(suggestedCircuitMatch.meanDistanceM)} m mean gap)`
                : ' (track metadata)'}
            </Alert>
          ) : null}

          <FormControl fullWidth size="small" disabled={!canEdit || contributionsLoading}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: chrome.label, mb: 0.5 }}>
              Circuit for this session
            </Typography>
            <SessionCircuitPicker
              options={circuitPickerOptions}
              selectedContributionId={selectedContributionId}
              disabled={!canEdit || contributionsLoading}
              onChange={(contributionId) => void assignCircuit(contributionId)}
            />
          </FormControl>

          {assignedContribution && startFinishStatus === 'loading' ? (
            <Typography sx={{ fontSize: '0.68rem', color: chrome.lapOut }}>
              Loading circuit geometry…
            </Typography>
          ) : assignedContribution && startFinishStatus === 'missing' ? (
            <Alert severity="warning" sx={{ py: 0.35 }}>
              This circuit is missing a usable start/finish line. Finish its circuit geometry before
              regenerating laps.
            </Alert>
          ) : null}

          {resolvedCircuit.overlay?.startFinishLine ? (
            <CircuitLapResliceSettings
              compact
              hideTimingToggle
              reslice={reslice ?? { laps: [], crossingsMs: [] }}
              useFixedLaps={circuitLapReslice.useFixedLaps}
              onUseFixedLapsChange={circuitLapReslice.setUseFixedLaps}
              onRegenerate={circuitLapReslice.regenerate}
              onSave={() => void circuitLapReslice.saveFixedLaps()}
              onClearSaved={
                canEdit ? () => void circuitLapReslice.clearSavedFixedLaps() : undefined
              }
              saving={circuitLapReslice.saving}
              hasDraft={circuitLapReslice.hasDraft}
              hasSaved={circuitLapReslice.hasSaved}
              savedAt={circuitLapReslice.savedAt}
              circuitName={resolvedCircuit.overlay?.name ?? assignedContribution?.venue}
              circuitSourceLabel={circuitSourceLabel}
              assignedContributionId={
                sessionContext?.contributionId ??
                (slot.publicDemo ? resolvedCircuit.contributionId : null)
              }
              resolvedContributionId={resolvedCircuit.contributionId}
              canEdit={canEdit}
              saveError={circuitLapReslice.saveError}
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: chrome.title }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
